import type { CaipNetwork } from "@reown/appkit";
import {
  arbitrum as arbitrumBase,
  base as baseBase,
  hoodi as hoodiBase,
  kaia as kaiaBase,
  mainnet as mainnetBase,
  optimism as optimismBase,
} from "viem/chains";

// TODO: Change to Blockdaemon RPC once auth is setup (https://svc.blockdaemon.com/ethereum/hoodi/native)

/**
 * Global asset configuration for the widget
 * This defines which assets and networks are supported across the application
 */
export const hoodi: CaipNetwork = {
  ...hoodiBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:560048",
  rpcUrls: {
    default: {
      // TODO: Change to Blockdaemon RPC once auth is setup (https://svc.blockdaemon.com/ethereum/hoodi/native)
      http: ["https://ethereum-hoodi-rpc.publicnode.com/"],
    },
  },
};

const mainnet: CaipNetwork = {
  ...mainnetBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:1",
  rpcUrls: {
    default: {
      http: ["https://eth.merkle.io/"],
    },
  },
};

const optimism: CaipNetwork = {
  ...optimismBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:10",
  rpcUrls: {
    default: {
      http: ["https://mainnet.optimism.io"],
    },
  },
};

const base: CaipNetwork = {
  ...baseBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:8453",
  rpcUrls: {
    default: {
      http: ["https://base.llamarpc.com"],
    },
  },
};

const arbitrum: CaipNetwork = {
  ...arbitrumBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:42161",
  rpcUrls: {
    default: {
      http: ["https://arbitrum.drpc.org"],
    },
  },
};

const kaia: CaipNetwork = {
  ...kaiaBase,
  chainNamespace: "eip155",
  caipNetworkId: `eip155:${kaiaBase.id}`,
  rpcUrls: {
    default: {
      http: ["https://public-en.node.kaia.io"],
    },
  },
};

interface AssetConfig {
  symbol: string;
  name: string;
  networks: Array<CaipNetwork>; // Network configuration objects
}

/**
 * Module-specific asset configurations
 * Each array explicitly defines which assets and networks that module supports
 */

// DeFi module: Supports ETH on specific networks (can be filtered further by config)
export const DEFI_ASSETS: AssetConfig[] = [
  {
    symbol: "eth",
    name: "Ethereum",
    networks: [hoodi, arbitrum, base, mainnet, optimism],
  },
];

// Rewards module: Supports ETH on specific networks
export const REWARDS_ASSETS: AssetConfig[] = [
  {
    symbol: "eth",
    name: "Ethereum",
    networks: [hoodi, mainnet],
  },
];

// Swap module: Supports ETH on specific networks
export const SWAP_ASSETS: AssetConfig[] = [
  {
    symbol: "eth",
    name: "Ethereum",
    networks: [hoodi, arbitrum, base, mainnet, kaia],
  },
];

// Portfolio module: Currently no restrictions (empty array = all networks allowed)
export const PORTFOLIO_ASSETS: AssetConfig[] = [];

// Discover module: Currently no restrictions (empty array = all networks allowed)
export const DISCOVER_ASSETS: AssetConfig[] = [];

/**
 * Get all unique networks across DeFi and Rewards modules
 * Used for wallet initialization
 */
function getAllAssets(): AssetConfig[] {
  const allAssets = [...DEFI_ASSETS, ...REWARDS_ASSETS];
  const networkMap = new Map<number, CaipNetwork>();

  // Collect all unique networks
  for (const asset of allAssets) {
    for (const network of asset.networks) {
      const id = typeof network.id === "number" ? network.id : Number(network.id);
      networkMap.set(id, network);
    }
  }

  return [
    {
      symbol: "eth",
      name: "Ethereum",
      networks: Array.from(networkMap.values()),
    },
  ];
}

/**
 * Get network configurations from assets
 */
export function getSupportedNetworks(assets: AssetConfig[] = []): CaipNetwork[] {
  if (assets.length === 0) {
    return getAllAssets().flatMap((asset) => asset.networks);
  }
  return assets.flatMap((asset) => asset.networks);
}

/**
 * Get all supported asset symbols in lowercase
 * @param assets - Optional array of assets to get symbols from (defaults to all assets)
 */
export function getSupportedAssetSymbols(assets?: AssetConfig[]): string[] {
  const assetsToUse = assets || getAllAssets();
  return assetsToUse.map((asset) => asset.symbol);
}

/**
 * Check if an asset symbol is supported
 * @param symbol - Asset symbol to check
 * @param assets - Optional array of assets to check against (defaults to all assets)
 */
export function isAssetSupported(symbol: string, assets?: AssetConfig[]): boolean {
  const normalizedSymbol = symbol.toLowerCase();
  return getSupportedAssetSymbols(assets).includes(normalizedSymbol);
}
